import { EnrollmentRequiredError, WalletSyncEngine } from "./WalletSyncEngine.js";

export interface SyncScheduler {
  schedulePull(): void;
  schedulePush(): void;
}

export interface SyncSchedulerOptions {
  pullOnResume?: () => boolean;
  pushDebounceMs?: number;
  onError?: (error: Error) => void;
}

export function createSyncScheduler(
  engine: WalletSyncEngine,
  options: SyncSchedulerOptions = {},
): SyncScheduler & { destroy(): void } {
  const pushDebounceMs = options.pushDebounceMs ?? 400;
  let pushTimer: ReturnType<typeof setTimeout> | null = null;
  let pullInFlight: Promise<void> | null = null;

  const onError = (error: Error): void => {
    options.onError?.(error);
  };

  const schedulePull = (): void => {
    if (pullInFlight) return;
    pullInFlight = engine
      .pull()
      .catch((error: unknown) => {
        if (error instanceof EnrollmentRequiredError) {
          return;
        }
        onError(error instanceof Error ? error : new Error(String(error)));
      })
      .finally(() => {
        pullInFlight = null;
      });
  };

  const schedulePush = (): void => {
    if (pushTimer) {
      clearTimeout(pushTimer);
    }
    pushTimer = setTimeout(() => {
      pushTimer = null;
      engine.push().catch((error: unknown) => {
        onError(error instanceof Error ? error : new Error(String(error)));
      });
    }, pushDebounceMs);
  };

  const shouldPullOnResume = (): boolean => {
    if (options.pullOnResume) {
      return options.pullOnResume();
    }
    if (typeof document !== "undefined") {
      return document.visibilityState === "visible";
    }
    return true;
  };

  const handleVisibilityChange = (): void => {
    if (shouldPullOnResume()) {
      schedulePull();
    }
  };

  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", handleVisibilityChange);
  }

  engine.attachScheduler({ schedulePull, schedulePush });

  return {
    schedulePull,
    schedulePush,
    destroy(): void {
      if (pushTimer) {
        clearTimeout(pushTimer);
        pushTimer = null;
      }
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
      }
      engine.detachScheduler();
    },
  };
}
